import type { Result } from '@/types/result';
import { err, ok } from '@/types/result';
import type { ValidationError } from './errors';
import { makeError } from './errors';

/**
 * A validator turns unknown input into a typed value, or reports one or more
 * accumulated errors. Path threading lets composite validators produce useful
 * error messages like `notes[2].width`.
 */
export type Validator<Out> = (input: unknown, path?: string) => Result<Out, ValidationError[]>;

// --- primitives ---------------------------------------------------------------

export function string(opts: { min?: number; max?: number } = {}): Validator<string> {
  return (input, path = '') => {
    if (typeof input !== 'string') {
      return err([makeError(path, 'not_a_string', 'expected a string')]);
    }
    const errors: ValidationError[] = [];
    if (opts.min !== undefined && input.length < opts.min) {
      errors.push(makeError(path, 'too_short', `min length ${opts.min}`));
    }
    if (opts.max !== undefined && input.length > opts.max) {
      errors.push(makeError(path, 'too_long', `max length ${opts.max}`));
    }
    return errors.length ? err(errors) : ok(input);
  };
}

export function number(
  opts: { min?: number; max?: number; int?: boolean; finite?: boolean } = {},
): Validator<number> {
  const finite = opts.finite ?? true;
  return (input, path = '') => {
    if (typeof input !== 'number' || Number.isNaN(input)) {
      return err([makeError(path, 'not_a_number', 'expected a number')]);
    }
    if (finite && !Number.isFinite(input)) {
      return err([makeError(path, 'not_finite', 'expected a finite number')]);
    }
    const errors: ValidationError[] = [];
    if (opts.int && !Number.isInteger(input)) {
      errors.push(makeError(path, 'not_an_integer', 'expected an integer'));
    }
    if (opts.min !== undefined && input < opts.min) {
      errors.push(makeError(path, 'below_min', `must be >= ${opts.min}`));
    }
    if (opts.max !== undefined && input > opts.max) {
      errors.push(makeError(path, 'above_max', `must be <= ${opts.max}`));
    }
    return errors.length ? err(errors) : ok(input);
  };
}

export function literal<T extends string | number | boolean>(expected: T): Validator<T> {
  return (input, path = '') => {
    if (input === expected) return ok(expected);
    return err([makeError(path, 'literal_mismatch', `expected literal ${String(expected)}`)]);
  };
}

export function oneOf<T extends string>(values: readonly T[]): Validator<T> {
  return (input, path = '') => {
    if (typeof input === 'string' && (values as readonly string[]).includes(input)) {
      return ok(input as T);
    }
    return err([
      makeError(path, 'not_in_enum', `must be one of: ${values.join(', ')}`),
    ]);
  };
}

// --- composites ---------------------------------------------------------------

export function array<T>(item: Validator<T>): Validator<T[]> {
  return (input, path = '') => {
    if (!Array.isArray(input)) {
      return err([makeError(path, 'not_an_array', 'expected an array')]);
    }
    const errors: ValidationError[] = [];
    const values: T[] = [];
    for (let i = 0; i < input.length; i++) {
      const r = item(input[i], `${path}[${i}]`);
      if (r.ok) values.push(r.value);
      else errors.push(...r.error);
    }
    return errors.length ? err(errors) : ok(values);
  };
}

type ShapeSpec = Record<string, Validator<unknown>>;
type InferShape<S extends ShapeSpec> = { [K in keyof S]: ReturnType<S[K]> extends Result<infer T, unknown> ? T : never };

/**
 * Accumulates errors across all fields in one pass so users see everything
 * that's wrong, not just the first problem.
 */
export function shape<S extends ShapeSpec>(spec: S): Validator<InferShape<S>> {
  return (input, path = '') => {
    if (typeof input !== 'object' || input === null || Array.isArray(input)) {
      return err([makeError(path, 'not_an_object', 'expected an object')]);
    }
    const obj = input as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    const errors: ValidationError[] = [];
    for (const key of Object.keys(spec)) {
      const fieldValidator = spec[key];
      if (!fieldValidator) continue;
      const fieldPath = path ? `${path}.${key}` : key;
      const r = fieldValidator(obj[key], fieldPath);
      if (r.ok) out[key] = r.value;
      else errors.push(...r.error);
    }
    return errors.length ? err(errors) : ok(out as InferShape<S>);
  };
}

export function refine<T>(
  base: Validator<T>,
  predicate: (value: T) => boolean,
  message: string,
): Validator<T> {
  return (input, path = '') => {
    const r = base(input, path);
    if (!r.ok) return r;
    if (!predicate(r.value)) {
      return err([makeError(path, 'failed_refinement', message)]);
    }
    return r;
  };
}

export function nullable<T>(base: Validator<T>): Validator<T | null> {
  return (input, path = '') => (input === null ? ok(null) : base(input, path));
}

export function optional<T>(base: Validator<T>): Validator<T | undefined> {
  return (input, path = '') => (input === undefined ? ok(undefined) : base(input, path));
}
